/**
 * JourneyDomainAssignment (DC-1617)
 *
 * Per-journey control to assign, reassign, or clear the triangulation domain of
 * an EXISTING journey. Pairs with the create-time DomainSelect in
 * JourneyCreationForm — DC-1493 only let you set the domain at creation, so a
 * journey created before its domain existed could never surface under
 * /triangulation/:domainId without being re-created. This closes that gap.
 *
 * Captain-only: the PATCH /api/journey/:group_id/domain endpoint is captain-
 * gated server-side (403 otherwise); the UI mirrors that by disabling the
 * control for non-captains and showing a read-only note. The current
 * assignment is read from GET /api/journey/:group_id — `metadata.domain_id`,
 * which the store mapper sources from the authoritative `domain_id` column.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { DomainSelect } from './DomainSelect';

interface JourneyDomainAssignmentProps {
  journeyId: string;
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

interface JourneyGetResponse {
  journey?: {
    captain?: { user_id?: string };
    metadata?: { domain_id?: string | null };
  };
}

interface DomainPatchResponse {
  journey?: { domain_id?: string | null };
}

export const JourneyDomainAssignment: React.FC<JourneyDomainAssignmentProps> = ({
  journeyId,
}) => {
  // Stable refs — same pattern DomainSelect documents to avoid the jsdom
  // infinite-render bug from calling useApiClient() inside a dep array.
  const { get, patch, userId } = useApiClient();

  const [domainId, setDomainId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // skipCache: the assignment can change via this very control, so always
      // read the freshest journey rather than a 5-minute-cached copy.
      const data = await get<JourneyGetResponse>(`/api/journey/${journeyId}`, {
        skipCache: true,
      });
      setDomainId(data?.journey?.metadata?.domain_id ?? null);
      setCanEdit(Boolean(userId) && data?.journey?.captain?.user_id === userId);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load journey domain');
    } finally {
      setLoading(false);
    }
  }, [get, journeyId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleChange = useCallback(
    async (next: string | null) => {
      // Reassignment requires a domain — the control runs in `required` mode so
      // the empty "— No domain —" option is suppressed; guard defensively.
      if (next === null) return;
      const previous = domainId;
      setDomainId(next); // optimistic
      setSaveState({ kind: 'saving' });
      try {
        const data = await patch<DomainPatchResponse>(
          `/api/journey/${journeyId}/domain`,
          { domain_id: next },
        );
        setDomainId(data?.journey?.domain_id ?? null);
        setSaveState({ kind: 'saved' });
        // Auto-clear the "saved" indicator so it doesn't linger.
        setTimeout(() => {
          setSaveState((prev) => (prev.kind === 'saved' ? { kind: 'idle' } : prev));
        }, 2000);
      } catch (err) {
        setDomainId(previous); // rollback on failure
        setSaveState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to update domain',
        });
      }
    },
    [patch, journeyId, domainId],
  );

  return (
    <section
      aria-labelledby="journey-domain-heading"
      style={sectionStyle}
      data-testid="journey-domain-assignment"
    >
      <h3 id="journey-domain-heading" style={sectionHeadingStyle}>
        Triangulation domain
      </h3>
      <p style={helpTextStyle}>
        Assigning this journey to an organizational data domain makes its FAIR
        results contribute to that domain&rsquo;s Triangulation view. You can
        move it to a different domain at any time.
      </p>

      {loading && (
        <div style={statusRowStyle} role="status" aria-live="polite">
          <Loader2 size={16} className="spin" aria-hidden="true" />
          <span>Loading current assignment&hellip;</span>
        </div>
      )}

      {loadError && (
        <div style={errorRowStyle} role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{loadError}</span>
          <button type="button" onClick={() => void load()} style={retryButtonStyle}>
            Retry
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <DomainSelect
            id="journey-domain-select"
            label="Domain"
            required
            value={domainId}
            onChange={handleChange}
            disabled={!canEdit || saveState.kind === 'saving'}
            helpText={
              canEdit
                ? 'Select the data domain this journey belongs to. It will contribute to that domain’s Triangulation view.'
                : 'Only the journey captain can change the triangulation domain.'
            }
          />

          {saveState.kind === 'saving' && (
            <div style={statusRowStyle} role="status" aria-live="polite">
              <Loader2 size={16} className="spin" aria-hidden="true" />
              <span>Saving&hellip;</span>
            </div>
          )}

          {saveState.kind === 'saved' && (
            <div style={savedRowStyle} role="status" aria-live="polite">
              <Check size={16} aria-hidden="true" />
              <span>Saved</span>
            </div>
          )}

          {saveState.kind === 'error' && (
            <div style={errorRowStyle} role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{saveState.message}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
};

// ============================================================================
// STYLES — inline to match JourneySettingsPanel's convention (kept local while
// the v1.2 UX overhaul with Larissa is in flight).
// ============================================================================

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--line, #1c2a54)',
  borderRadius: '8px',
  padding: '1.25rem',
  background: 'var(--pane, #0f1730)',
  marginTop: '1.5rem',
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--text, #e8eefc)',
};

const helpTextStyle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '0.875rem',
  color: 'var(--text-secondary, #b8c5dc)',
  lineHeight: 1.5,
};

const statusRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.75rem',
  fontSize: '0.875rem',
  color: 'var(--text-secondary, #b8c5dc)',
};

const savedRowStyle: React.CSSProperties = {
  ...statusRowStyle,
  color: 'var(--success-text, #68d391)',
};

const errorRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.75rem',
  fontSize: '0.875rem',
  color: 'var(--error, #e74c3c)',
};

const retryButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '0.25rem 0.625rem',
  fontSize: '0.8125rem',
  border: '1px solid currentColor',
  borderRadius: '4px',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
};

export default JourneyDomainAssignment;
